import { usePersistedDataStore } from '../components/store';
import { useLocation } from 'react-router-dom';
import { useUser } from '../components/UserContext';
import axiosInstance from '../fetchConfig/axiosConfig';
import { SECONDARY_STYLE_MAPPINGS } from './mappings';
import logger from '../utils/logger';
import {
  validateInventoryUpdates,
  formatInventoryUpdateValidationErrors,
} from '../components/CaseReview/CaseReviewHelpers/inventoryUpdateProtections.js';

export class InventoryUpdateValidationError extends Error {
  constructor(message) {
    super(message);
    this.name = 'InventoryUpdateValidationError';
  }
}

const assertInventoryUpdatesValid = (records) => {
  const updatesByParcel = Object.fromEntries(
    records
      .filter((record) => record?.pid || record?.parcel_id)
      .map((record) => [String(record.pid || record.parcel_id), record])
  );
  const validation = validateInventoryUpdates(updatesByParcel);
  if (!validation.isValid) {
    throw new InventoryUpdateValidationError(formatInventoryUpdateValidationErrors(validation));
  }
};

// Field mappings for database column names
const DB_FIELD_MAPPINGS = {
  // Bath mappings
  'Bath': 'BathsFull',
  'HalfBath': 'BathsHalf',
  'fullBaths': 'BathsFull',
  'halfBaths': 'BathsHalf',
  
  // Style mapping
  'Style': 'BuildingStyle',
  // Square footage mapping
  'Sqft': 'LivingArea',
  // Garage mapping - there may still be a  bug here???
  'TotalGarageCap': 'GarageCapacity',
  'PropertyAssessment': 'Assessment',
};

// Backend field transformation logic for special composite fields
const transformBackendFields = (update) => {
  const transformed = { ...update };
  
  // Handle View field transformation
  if (transformed.View) {
    if (transformed.View === "None") {
      transformed['Waterfront'] = 0;
      transformed['Waterview'] = 0;
    } else if (transformed.View === "Waterview") {
      transformed['Waterview'] = 1;
      transformed['Waterfront'] = 0;
    } else {
      transformed[transformed.View] = 1;
    }
    delete transformed.View;
  }
  
  // Handle boolean singleSelect fields (None -> 0, True -> 1)
  const booleanSelectFields = {
    Pool: 'InGroundPool',
    Deck: 'Deck',
    Patio: 'Patio',
    Porch: 'Porch',
  };
  Object.entries(booleanSelectFields).forEach(([uiField, dbField]) => {
    const value = transformed[uiField];
    if (value === 'None' || value === 'True') {
      transformed[dbField] = value === 'None' ? 0 : 1;
      if (uiField !== dbField) {
        delete transformed[uiField];
      }
    }
  });

  // CAC dropdown: True -> 1, False -> 0
  if (transformed.CentralAir === 'True' || transformed.CentralAir === 'False') {
    transformed.CentralAir = transformed.CentralAir === 'True' ? 1 : 0;
  }
  
  // Handle TotalGarageCap to GarageCapacity mapping
  if (transformed.TotalGarageCap) {
    transformed['GarageCapacity'] = transformed.TotalGarageCap;
    delete transformed.TotalGarageCap;
  }
  
  // Handle Condition to ConditionIn mapping
  if (transformed.Condition) {
    transformed['ConditionIn'] = transformed.Condition;
    delete transformed.Condition;
  }
  
  // This remaps the styles that are no longer primary into secondary styles.
  if (transformed.Style) {
    // Check if this style needs to be remapped
    if (SECONDARY_STYLE_MAPPINGS[transformed.Style]) {
      transformed['SecondaryStyle'] = transformed.Style; // Keep original as secondary
      transformed['Style'] = SECONDARY_STYLE_MAPPINGS[transformed.Style]; // Remap primary style
    }
  }
  
  return transformed;
};

// rebuilt a simpler function to JUST update the inventory (send in an array of objects of PIDS with the updated inventory)
export async function updateDatabaseNewFunction(updatedValues, userId = null) {
  assertInventoryUpdatesValid(
    Object.entries(updatedValues).map(([pid, updates]) => ({ pid, ...updates }))
  );

  // If no userId provided, fall back to localStorage for backward compatibility
  const finalUserId = userId || JSON.parse(localStorage.getItem('userInfo')).userId;
  const queryParams = new URLSearchParams(window.location.search);
  const taxYear = queryParams.get('TaxYear') || '';

  // Transform the object into an array of records with mapped field names
  const records = Object.entries(updatedValues).map(([pid, updates]) => {
    const transformedUpdates = transformBackendFields(updates);
    const mappedUpdates = {};
    Object.entries(transformedUpdates).forEach(([field, value]) => {
      const dbField = DB_FIELD_MAPPINGS[field] || field;
      mappedUpdates[dbField] = value;
    });
    return {
      pid,
      ...mappedUpdates
    };
  });

  let inventoryUpdateObj = {
    UserId: finalUserId,
    TaxYear: taxYear,
    Records: records,
    reRunKNN: false,
  };
  logger.log('inventoryUpdateObj', inventoryUpdateObj)

  try {
    const response = await axiosInstance.post('/update_inventory', inventoryUpdateObj);
    if (response.data.error) {
      throw new Error(response.data.error);
    }
    return response.data;
  } catch (error) {
    logger.error('Error updating database:', error);
    throw error;
  }
}

export default function useUpdateDB() {
  const persistedDataStore = usePersistedDataStore();
  const location = useLocation();
  const { user } = useUser();
  const negotiationObj = persistedDataStore.negotiationObj;
  const queryParams = new URLSearchParams(location.search);
  const comp = parseInt(queryParams.get('comp')) - 1 || 0;
  const courtDate = queryParams.get('CourtDate') || '';
  const muniCode = queryParams.get('MuniCode') || '';
  const taxYear = queryParams.get('TaxYear') || '';

  async function updateDB({ confirmed = false, handleConfirmCancel, updatedValues }) {
    assertInventoryUpdatesValid(updatedValues);

    const parcelIds = negotiationObj.uids
    const userId = user?.userId;
    const [month, day, year] = courtDate ? courtDate.split('/') : [null, null, null];
    const courtDateFormatted = year
      ? [year, month.padStart(2, '0'), day.padStart(2, '0')].join('-')
      : null;
    const cases = negotiationObj.cases

    // Transform updatedValues to ensure each record has a pid field for inventory updates
    const records = updatedValues.map(record => {
      logger.log('record coming into update db', record);
      // Ensure the record has a pid field for backend identification
      const baseRecord = !record.pid && record.parcel_id 
        ? { ...record, pid: record.parcel_id }
        : record;
      
      // Apply backend field transformations for special composite fields
      const transformedRecord = transformBackendFields(baseRecord);
      
      // Apply field mappings to fix problematic field names
      const mappedRecord = {};
      Object.entries(transformedRecord).forEach(([field, value]) => {
        const dbField = DB_FIELD_MAPPINGS[field] || field;
        mappedRecord[dbField] = value;
      });
      
      return mappedRecord;
    });

    // Use comp to extract the subj from pid
    let inventoryUpdateObj = {
      CourtRun: courtDate ? 1 : 0,
      MuniCode: muniCode,
      TaxYear: taxYear,
      UserId: userId,
      Records: records,
      uids: parcelIds,
      cases: cases,
      updatedSubject: parcelIds[comp], // Use comp to extract the subj from pid
      // originalCompsheet: persistedDataStore.compSheet,
      // originalOptimizedComps: persistedDataStore.optimizedComps,
      ...(courtDateFormatted && { CourtDate: courtDateFormatted }), // Conditionally include CourtDate
    };

    // logger.log('Final inventoryUpdateObj:', inventoryUpdateObj);
    // Return the inventoryUpdateObj here
    return inventoryUpdateObj;
  }

  return { updateDB };
}